<template>
  <nav class="main_menu" aria-label="Primary">
    <router-link :to="{name: 'main-page'}" class="mobile-logo" v-if="!userIsLoggedInState">
      <img :src="$getAsset('/assets/mcr_logo_dude_transparent.png')" alt="logo" />
    </router-link>
    <router-link :to="{name: 'main-page'}" class="logo">
      <img :src="$getAsset('/assets/mcr_logo_white_transparent.png')" alt="logo" />
    </router-link>
    <ul class="main_menu_list">
      <main-menu-item
        v-for="(item, index) of displayMenuItems"
        :key="index"
        :text="item.text"
        :mobile-text="item.mobileText"
        :mobile-se-text="item.mobileSeText"
        :sub-menu="item.subMenu"
        :is-user-item="item.isUserItem"
        :is-login-item="item.isLoginItem"
        :is-button-router-link-item="item.isButtonRouterLinkItem"
        :route="item.route"
        :rel="item.rel"
        :icon="item.icon"
        :icon-alt-text="item.iconAltText"
        :tooltip="item.tooltip"
        :need-review="item.needReview"
        :separated="item.separated"
        :is-mobile-view="isMobileView"
        :class="item.classes"
        :click-handler="item.clickHandler"
      ></main-menu-item>
    </ul>
  </nav>
</template>

<script>
import MainMenuItem from '@common/elements/main-menu/main.menu.item';
import AnalyticsMainHandler from '@common/utils/analytics/analytics.main';
import {TAB_ID_AI_SEARCH, TAB_ID_FULL_TEXT, TAB_ID_INDEXED_RECORDS} from '@common/utils/consts.search';
import {sourceTypeCategories} from '@common/utils/consts.source';
import {stopImpersonation} from '@common/utils/impersonation';
import {getFamilyTreeRoute, getFamilyTreeLibraryRoute} from '@common/utils/utils.routes';
import {mapGetters} from 'vuex';

import ShortcutsModalContent from '@/components/modules/familyTree/tree/modals/ShortcutsModalContent';

export default {
  computed: {
    ...mapGetters([
      'userEmailState',
      'userIsLoggedInState',
      'userIsSharedState',
      'userIsSubscribedState',
      'userIsStaffState',
      'userFirstNameState',
      'userFamilyTreeIdState',
      'treeOwnershipDataState',
      'userHintsTreeIdState',
      'impersonatedUserEmailState',
      'userFwsUsernameState',
      'userHasResearchReportsState',
      'windowWidthState',
      'featureSwitchesState',
      'featureSwitchesLoadedState',
      'reviewedItemsState',
      'activeFamilyTreeIdState',
    ]),
    displayMenuItems() {
      return this.menuItems.filter(item => {
        return this.windowWidthState >= (item.minScreen || 0) && this.windowWidthState <= (item.maxScreen || Infinity);
      });
    },
    userMainTreeId() {
      if (this.userIsStaffState && this.treeOwnershipDataState && this.treeOwnershipDataState.family_tree_id) {
        return this.treeOwnershipDataState.family_tree_id;
      }

      return this.userFamilyTreeIdState;
    },
    ownedActiveTreeIds() {
      const trees = this.treeOwnershipDataState?.family_trees || [];
      return trees.map(tree => tree.object_id.toString());
    },
    activeTreeId() {
      if (this.userIsStaffState && this.ownedActiveTreeIds.includes(`${this.activeFamilyTreeIdState}`)) {
        return this.activeFamilyTreeIdState;
      }

      if (this.userIsStaffState && this.treeOwnershipDataState && this.treeOwnershipDataState.family_tree_id) {
        return this.treeOwnershipDataState.family_tree_id;
      }

      return this.activeFamilyTreeIdState;
    },
    familyTreeMenuRoute() {
      if (this.userIsLoggedInState && !this.userIsSharedState && this.activeTreeId) {
        return {name: 'familytree-details', params: {id: this.activeTreeId}};
      }

      return getFamilyTreeRoute(this.$store);
    },
    familyLibraryMenuRoute() {
      if (this.userIsLoggedInState && !this.userIsSharedState && this.activeTreeId) {
        return {name: 'familytree-library', params: {id: this.activeTreeId}};
      }

      return getFamilyTreeLibraryRoute(this.$store);
    },
    familyClansMenuItem() {
      if (!this.userIsLoggedInState || this.userIsSharedState || !this.familyTreeMenuRoute.params) {
        return null;
      }

      return this.getRouterLinkItem('Clans', 'familytree-clans', {id: this.familyTreeMenuRoute.params.id});
    },
    familyStoriesMenuItem() {
      if (this.userIsLoggedInState && this.userMainTreeId) {
        return this.getRouterLinkItem('Stories & Compilations', 'stories-list', {treeId: this.userMainTreeId});
      }

      return this.getRouterLinkItem('Stories & Compilations', 'village-exploration-service');
    },
    familyDiscoveriesMenuItem() {
      if (this.userIsLoggedInState && this.userMainTreeId) {
        return this.getRouterLinkItem('New Discoveries', 'research-suggestions', {treeId: this.userMainTreeId});
      }

      return this.getRouterLinkItem('RecordFinder™', 'familytree-hints-landing');
    },
    familySubMenu() {
      const viewTreeLabel = this.userIsLoggedInState ? 'View Tree' : 'Build Your Tree';
      const savedRecordsItem =
        this.userIsLoggedInState && this.userMainTreeId
          ? this.getRouterLinkItem('Saved Records', 'saved-records', {treeId: this.userMainTreeId})
          : this.getRouterLinkItem('Saved Records', 'my-records');

      return [
        this.getRouterLinkItem(viewTreeLabel, this.familyTreeMenuRoute.name, this.familyTreeMenuRoute.params),
        this.familyStoriesMenuItem,
        this.familyDiscoveriesMenuItem,
        this.getRouterLinkItem('Image to Text', 'image-to-text'),
        this.getRouterLinkItem('Photos & Files', this.familyLibraryMenuRoute.name, this.familyLibraryMenuRoute.params),
        this.familyClansMenuItem,
        savedRecordsItem,
      ].filter(Boolean);
    },
    researchSubMenu() {
      return [
        this.getRouterLinkItem('Research Center', 'research'),
        this.getRouterLinkItem('AI Skills', 'agent-skills'),
        this.getRouterLinkItem('Unified Search', 'research-unified-search'),
      ];
    },
    menuItems() {
      let searchRecordsSubMenu = [
        this.getRouterLinkItem('All Records', 'search-all-records', null, {
          tab: this.featureSwitchesState.SEARCH_AI ? TAB_ID_AI_SEARCH : TAB_ID_INDEXED_RECORDS,
        }),
        this.getRouterLinkItem('Zupus (Family Tree Books)', 'search-all-records', null, {
          tab: this.featureSwitchesState.SEARCH_AI ? TAB_ID_AI_SEARCH : TAB_ID_FULL_TEXT,
          category_id: sourceTypeCategories.CATEGORY_ZUPUS,
        }),
        this.getRouterLinkItem('Birth, Marriage & Death', 'search-all-records', null, {
          tab: this.featureSwitchesState.SEARCH_AI ? TAB_ID_AI_SEARCH : TAB_ID_INDEXED_RECORDS,
          category_id: sourceTypeCategories.CATEGORY_BIRTH_DEATH,
        }),
        this.getRouterLinkItem('Immigration & Travel', 'search-all-records', null, {
          tab: this.featureSwitchesState.SEARCH_AI ? TAB_ID_AI_SEARCH : TAB_ID_INDEXED_RECORDS,
          category_id: sourceTypeCategories.CATEGORY_IMMIGRATION,
        }),
        this.getRouterLinkItem('Census Records', 'search-all-records', null, {
          tab: this.featureSwitchesState.SEARCH_AI ? TAB_ID_AI_SEARCH : TAB_ID_INDEXED_RECORDS,
          category_id: sourceTypeCategories.CATEGORY_CENSUS,
        }),
        this.getRouterLinkItem('Chinese Surnames', 'search-tool-surname'),
        this.getRouterLinkItem('Chinese Villages', 'search-tool-village'),
        this.getRouterLinkItem('Overseas Chinese Communities', 'search-overseas'),
      ];
      let resourcesSubMenu = [
        this.getRouterLinkItem('Genealogy Wiki', 'wiki-index'),
        this.getExternalLinkItem('Blog', '/blog'),
        this.getRouterLinkItem('Discord Community', 'community'),
        this.getRouterLinkItem('Custom Research', 'services-main'),
        this.getExternalLinkItem('For Institutions', process.env.VUE_APP_LIBRARY_SITE_BASE),
      ];
      resourcesSubMenu.splice(2, 0, this.getRouterLinkItem('Workshops & Events', 'events'));

      let helpSubMenu = [
        this.getRouterLinkItem('Support', 'support', null, null, 'nofollow'),
        {
          text: 'Shortcuts',
          clickHandler: () => {
            const modalParams = {
              classes: 'clear_modal white_modal',
              name: 'shortcutsmodal',
              height: 'auto',
              scrollable: true,
            };
            this.$modal.show(ShortcutsModalContent, {}, modalParams);
          },
        },
      ];

      let subscriptionPlans = this.getRouterLinkItem('Plans', 'subscription-plans', null, null, 'nofollow');
      let moreSubMenu = [...resourcesSubMenu];
      if (!this.userIsSharedState && !this.userIsSubscribedState) {
        moreSubMenu.unshift(subscriptionPlans);
      }
      if (!this.userIsLoggedInState) {
        moreSubMenu.push({
          isButtonRouterLinkItem: true,
          text: 'Get Started for Free',
          route: {name: 'register', query: {redirect: this.redirect}},
        });
      }
      moreSubMenu.push({text: 'Help'}, ...helpSubMenu);
      let menu = [
        {text: 'Search', subMenu: searchRecordsSubMenu},
        {text: 'Family Tree', subMenu: this.familySubMenu, mobileSeText: 'Tree'},
        ...(this.userIsStaffState ? [{text: 'Research', subMenu: this.researchSubMenu}] : []),
        {text: 'Resources', subMenu: resourcesSubMenu, minScreen: this.$breakpoints.phablet},
        {text: 'Help', subMenu: helpSubMenu, minScreen: this.$breakpoints.phablet},
        {text: 'More', subMenu: moreSubMenu, maxScreen: this.$breakpoints.phablet},
      ];
      if (!this.userIsSharedState && !this.userIsSubscribedState) {
        menu.push({...subscriptionPlans, minScreen: this.$breakpoints.phablet});
      }
      if (this.userIsLoggedInState) {
        menu.push(this.userMenu);
      } else {
        menu.push({isLoginItem: true, route: {name: 'login', query: {redirect: this.redirect}}});
      }
      return menu;
    },
    userMenu() {
      return {
        isUserItem: true,
        subMenu: this.subMenuItems,
        separated: true,
      };
    },
    isMobileView() {
      return this.windowWidthState <= this.$breakpoints.mainMenu;
    },
    subMenuItems() {
      const logout = this.getLogoutMenuItem();
      let impersonate_menus = [];

      if (this.impersonatedUserEmailState) {
        const imperson = {text: `Impersonating ${this.impersonatedUserEmailState}`};
        const stop_imperson = {
          text: `Stop Impersonation`,
          clickHandler: () => {
            stopImpersonation(this);
          },
        };

        impersonate_menus = [imperson, stop_imperson];
      }

      if (this.userIsSharedState) {
        return [...impersonate_menus, logout];
      }

      const account = this.getRouterLinkItem('Account', 'user-profile');
      const history = this.getRouterLinkItem('Billing History', 'my-orders');

      return [...impersonate_menus, account, history, logout];
    },
    redirect() {
      const excludeRoutes = ['login', 'forgotpwd', 'register'];
      return excludeRoutes.includes(this.$route.name) ? this.$route.query.redirect : this.$route.fullPath;
    },
  },
  methods: {
    getLogoutMenuItem() {
      return {
        text: 'Logout',
        clickHandler: () => {
          this.$store.dispatch('logoutAction');
          this.$router.push({name: 'login', query: {redirect: this.$route.fullPath}});
          AnalyticsMainHandler.resumeTracking(); // init as impersonation stopped
          AnalyticsMainHandler.logout();
        },
      };
    },
    getRouterLinkItem(text, routerName, routeParams, routeQuery, rel) {
      return {text: text, route: {name: routerName, params: routeParams || {}, query: routeQuery || {}}, rel};
    },
    getExternalLinkItem(text, externalLink, icon) {
      return {text, clickHandler: () => window.open(externalLink, '_blank'), icon};
    },
  },
  components: {MainMenuItem},
};
</script>

<style lang="scss" scoped>
@use '@common/style/menu';
@use '@common/style/variables' as *;

.main_menu {
  width: 100vw;

  &::v-deep .button_link_item {
    margin: 0 16px 0 16px;
  }
  @media only screen and (min-width: $main-menu-breakpoint) {
    .main_menu_item:last-of-type {
      margin-right: 10px;
    }
  }

  @media only screen and (max-width: $main-menu-breakpoint) {
    &::v-deep .button_link_item {
      margin: 0 8px 0 8px;
    }
    .main_menu_item.right-side {
      margin-left: auto;
    }
  }
}
</style>
